/**
 * The demo clock shared by the mock modules (Pay, HR Ops, Billing).
 *
 * QA pins "now" with `?demoNow=YYYY-MM-DDTHH:mm` so values can be compared exactly with the
 * prototype oracle (run at 2026-09-25T10:00+07:00). A value without an offset is read as
 * Asia/Phnom_Penh (+07:00, no DST). A malformed value is ignored and the real clock is used.
 * Callers pass `enabled: false` when their module's `*_DEMO` flag is off.
 */

/** The business timezone of every demo date (the Field Ops precedent, lead ruling Q8). */
export const DEMO_TZ = 'Asia/Phnom_Penh';

/** The query parameter that pins the demo clock. */
export const DEMO_NOW_PARAM = 'demoNow';

const SHAPE = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?(Z|[+-]\d{2}:?\d{2})?$/;

/**
 * Parse a `?demoNow=` value.
 * @param raw - `YYYY-MM-DD`, `YYYY-MM-DDTHH:mm[:ss]`, optionally with `Z` / `±HH:mm`.
 * @returns The pinned instant, or `null` when the value is missing or malformed.
 */
export function parseDemoNow(raw: unknown): Date | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  const m = SHAPE.exec(s);
  if (!m) return null;
  const [, y, mo, d, hh = '00', mm = '00', ss = '00', zone] = m;
  const month = Number(mo);
  const day = Number(d);
  const hour = Number(hh);
  const minute = Number(mm);
  const second = Number(ss);
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59 || second > 59) {
    return null;
  }
  const iso = `${y}-${mo}-${d}T${hh}:${mm}:${ss}${zone ?? '+07:00'}`;
  const t = new Date(iso);
  if (!Number.isFinite(t.getTime())) return null;
  // Reject impossible calendar dates (e.g. 2026-02-31) that Date silently rolls over.
  const wall = new Date(t.getTime() + 7 * 3600_000);
  if (!zone && (wall.getUTCDate() !== day || wall.getUTCMonth() + 1 !== month)) return null;
  return t;
}

/** Options for {@link demoNow}. */
export interface DemoNowOptions {
  /** The module's `*_DEMO` flag. When false the pin is ignored. Default true. */
  enabled?: boolean;
  /** The query string to read (default: `window.location.search`). */
  search?: string;
  /** The real clock (tests). Default `() => new Date()`. */
  clock?: () => Date;
}

function currentSearch(): string {
  try {
    return typeof window !== 'undefined' && window.location ? window.location.search : '';
  } catch {
    return '';
  }
}

/**
 * The pinned demo instant from the URL, if any.
 * @param opts - See {@link DemoNowOptions}.
 * @returns The pinned instant, or `null` (no pin, malformed pin, or demo disabled).
 */
export function pinnedDemoNow(opts: DemoNowOptions = {}): Date | null {
  if (opts.enabled === false) return null;
  const search = opts.search ?? currentSearch();
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(search).get(DEMO_NOW_PARAM);
  } catch {
    raw = null;
  }
  return parseDemoNow(raw);
}

/**
 * "Now" for demo engines: the `?demoNow=` pin when present, otherwise the real clock.
 * @param opts - See {@link DemoNowOptions}.
 * @returns The current (or pinned) instant — always a fresh `Date`.
 */
export function demoNow(opts: DemoNowOptions = {}): Date {
  const pinned = pinnedDemoNow(opts);
  if (pinned) return new Date(pinned.getTime());
  return opts.clock ? opts.clock() : new Date();
}

/**
 * The calendar date of an instant in Asia/Phnom_Penh.
 * @param d - The instant.
 * @returns `YYYY-MM-DD`.
 */
export function demoToday(d: Date): string {
  return new Date(d.getTime() + 7 * 3600_000).toISOString().slice(0, 10);
}
